import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload as UploadIcon, ArrowRight, ArrowLeft, CheckCircle, ArrowDown, Smartphone, Globe, DollarSign, FileText, Image, Mic, Video, User } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { defaultAdvancedSettings, AdvancedSettingsType } from "@/components/AdvancedSettings";
import { CostConfirmDialog } from "@/components/CostConfirmDialog";
import { parseWhatsAppZip } from "@/utils/whatsappParser";
import { estimateCost } from "@/utils/costEstimation";
import logo from "@/assets/whatai-logo.png";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";

interface ChatAnalysis {
  id: string;
  file_name: string;
  message_count: number;
  image_count: number;
  audio_count: number;
  document_count: number;
  language: string;
  created_at: string;
  processing_status: string;
  ai_summary: string | null;
  short_summary: string | null;
  deleted_at?: string | null;
}

const Upload = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { user, loading } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [file, setFile] = useState<File | null>(null);
  const [context, setContext] = useState("");
  const [language, setLanguage] = useState("he");
  const [whisperMode, setWhisperMode] = useState("transcribe");
  const [uploading, setUploading] = useState(false);
  const [advancedSettings] = useState<AdvancedSettingsType>(defaultAdvancedSettings);
  const [analyzing, setAnalyzing] = useState(false);
  const [fileStats, setFileStats] = useState<{
    imageCount: number;
    audioCount: number;
    videoCount: number;
    documentCount: number;
    contactCount: number;
  } | null>(null);
  const [showCostDialog, setShowCostDialog] = useState(false);
  const [estimatedUploadCost, setEstimatedUploadCost] = useState(0);
  const [currentBudget, setCurrentBudget] = useState<number | undefined>(undefined);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!loading && !user) {
      navigate("/auth");
    }
  }, [user, loading, navigate]);

  // Check phone verification status
  useEffect(() => {
    const checkPhoneVerification = async () => {
      if (!user) return;

      const { data } = await supabase
        .from("profiles")
        .select("phone_verified")
        .eq("id", user.id)
        .single();

      if (data && !data.phone_verified) {
        navigate("/verify-phone");
      }
    };

    checkPhoneVerification();
  }, [user, navigate]);

  // Check admin status
  useEffect(() => {
    if (user) {
      checkAdminStatus();
    }
  }, [user]);

  const checkAdminStatus = async () => {
    if (!user) return;

    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user.id)
      .eq("role", "admin")
      .single();

    setIsAdmin(!!data);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-subtle flex items-center justify-center">
        <div className="text-center">
          <img 
            src={logo} 
            alt="WhatAI Logo" 
            className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-4 animate-pulse"
          />
          <p className="text-muted-foreground">טוען...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  const handleNextStep = () => {
    if (currentStep === 1 && !file) {
      toast({
        title: "נא לבחור קובץ",
        description: "יש לבחור קובץ לפני המעבר לשלב הבא",
        variant: "destructive",
      });
      return;
    }
    if (currentStep < 3) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFileSelect = async (selectedFile: File) => {
    const fileName = selectedFile.name.toLowerCase();
    
    if (!fileName.endsWith('.zip')) {
      toast({
        title: "קובץ לא תקין",
        description: "אנא העלה קובץ ZIP בלבד",
        variant: "destructive",
      });
      return;
    }

    setFile(selectedFile);
    setFileStats(null);
    
    // Analyze file to get cost estimation
    setAnalyzing(true);
    try {
      const parsed = await parseWhatsAppZip(selectedFile, () => {});
      setFileStats({
        imageCount: parsed.stats.imageCount,
        audioCount: parsed.stats.audioCount,
        videoCount: parsed.stats.videoCount,
        documentCount: parsed.stats.documentCount,
        contactCount: parsed.stats.contactCount,
      });
      
      // Build description with all media types found
      const mediaTypes = [];
      if (parsed.stats.imageCount > 0) mediaTypes.push(`${parsed.stats.imageCount} תמונות`);
      if (parsed.stats.audioCount > 0) mediaTypes.push(`${parsed.stats.audioCount} אודיו`);
      if (parsed.stats.videoCount > 0) mediaTypes.push(`${parsed.stats.videoCount} סרטונים`);
      if (parsed.stats.documentCount > 0) mediaTypes.push(`${parsed.stats.documentCount} מסמכים`);
      if (parsed.stats.contactCount > 0) mediaTypes.push(`${parsed.stats.contactCount} אנשי קשר`);
      
      toast({
        title: "קובץ נותח",
        description: mediaTypes.length > 0 ? `נמצאו: ${mediaTypes.join(', ')}` : "הקובץ נותח בהצלחה",
      });
    } catch (error) {
      console.error("Error analyzing file:", error);
      toast({
        title: "אזהרת ניתוח",
        description: "לא ניתן היה לנתח את הקובץ",
        variant: "destructive",
      });
    } finally {
      setAnalyzing(false);
    }
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      await handleFileSelect(e.target.files[0]);
    }
  };

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      await handleFileSelect(e.dataTransfer.files[0]);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleSubmit = async () => {
    if (!file) {
      toast({
        title: "חסר קובץ",
        description: "נא לבחור קובץ",
        variant: "destructive",
      });
      return;
    }

    if (!fileStats) {
      toast({
        title: "נדרש ניתוח",
        description: "נא להמתין לניתוח הקובץ",
        variant: "destructive",
      });
      return;
    }

    // Calculate estimated cost
    const costEstimate = estimateCost(
      fileStats.imageCount,
      fileStats.audioCount,
      true, // includes summary
      isAdmin,
      fileStats.videoCount,
      fileStats.documentCount,
      fileStats.contactCount
    );
    setEstimatedUploadCost(costEstimate.estimatedCost);

    // Fetch current budget
    try {
      const { data } = await supabase
        .from("profiles")
        .select("budget")
        .eq("id", user.id)
        .single();
      
      if (data) {
        setCurrentBudget(parseFloat(String(data.budget || 0)));
      }
    } catch (error) {
      console.error("Error fetching budget:", error);
    }

    // Show confirmation dialog
    setShowCostDialog(true);
  };

  const proceedWithUpload = async () => {
    if (!file) return;

    // Check and deduct budget
    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("budget")
        .eq("id", user.id)
        .single();

      if (profile) {
        const currentBudget = parseFloat(String(profile.budget || 0));
        if (currentBudget < estimatedUploadCost) {
          toast({
            title: "תקציב לא מספיק",
            description: "נא להוסיף תקציב",
            variant: "destructive",
          });
          setShowCostDialog(false);
          return;
        }

        // Deduct from budget
        const newBudget = currentBudget - estimatedUploadCost;
        await supabase
          .from("profiles")
          .update({ budget: newBudget })
          .eq("id", user.id);
      }
    } catch (error) {
      console.error("Error handling budget:", error);
      toast({
        title: "שגיאת תקציב",
        description: "נכשל בעיבוד התקציב",
        variant: "destructive",
      });
      setShowCostDialog(false);
      return;
    }

    setShowCostDialog(false);
    setUploading(true);
    
    try {
      const fileName = file.name.toLowerCase();
      
      // Handle WhatsApp ZIP files (existing logic)
      toast({
        title: "מעלה",
        description: "מפרק את ייצוא הצ'אט...",
      });

      // Parse the ZIP file to extract messages and media
      const parsed = await parseWhatsAppZip(file, () => {});
      
      toast({
        title: "מעלה",
        description: "מעלה קבצי מדיה...",
      });

      // Create a temporary chat ID for storage paths
      const tempChatId = crypto.randomUUID();

      // Upload media files to storage and get permanent URLs
      const messagesWithStorageUrls = await Promise.all(
        parsed.messages.map(async (msg) => {
          if (msg.attachment?.url && msg.attachment.url.startsWith('blob:')) {
            try {
              // Fetch the blob
              const blobResponse = await fetch(msg.attachment.url);
              const blob = await blobResponse.blob();
              
              // Generate storage path with user ID as first folder (required by RLS)
              const fileExtension = msg.attachment.fileName.split('.').pop() || 'jpg';
              const storagePath = `${user.id}/${tempChatId}/media/${msg.id}.${fileExtension}`;
              
              // Upload to storage
              const { error: uploadError } = await supabase.storage
                .from('chat-exports')
                .upload(storagePath, blob, {
                  contentType: blob.type,
                  upsert: true
                });
              
              if (uploadError) {
                console.error(`Failed to upload ${msg.attachment.fileName}:`, uploadError);
                return msg;
              }
              
              // Get signed URL (valid for 1 year)
              const { data: signedUrlData, error: signedUrlError } = await supabase.storage
                .from('chat-exports')
                .createSignedUrl(storagePath, 31536000); // 1 year
              
              if (signedUrlError || !signedUrlData) {
                console.error(`Failed to get signed URL for ${msg.attachment.fileName}:`, signedUrlError);
                return msg;
              }
              
              // Return message with storage path and signed URL
              return {
                ...msg,
                attachment: {
                  ...msg.attachment,
                  url: signedUrlData.signedUrl,
                  storagePath: storagePath
                }
              };
            } catch (error) {
              console.error(`Error uploading ${msg.attachment.fileName}:`, error);
              return msg;
            }
          }
          return msg;
        })
      );

      // Generate HTML content from messages
      const generateHTMLContent = (messages: any[]) => {
        return messages.map(msg => {
          const time = msg.timestamp ? new Date(msg.timestamp).toLocaleString() : '';
          const sender = msg.sender || 'Unknown';
          const content = msg.content || '';
          let html = `<div data-message-id="${msg.id}"><strong>${sender}</strong> (${time}): ${content}`;
          
          // Add attachment div with proper data attributes if attachment exists
          if (msg.attachment) {
            html += `\n<div class="attachment" data-type="${msg.attachment.type}" data-file="${msg.attachment.fileName || ''}" data-storage-path="${msg.attachment.storagePath || ''}"></div>`;
          }
          
          html += `</div>`;
          return html;
        }).join('\n');
      };

      const htmlContent = generateHTMLContent(messagesWithStorageUrls);

      // Create chat analysis record with parsed data
      const { data: chatRecord, error: insertError } = await supabase
        .from('chat_analyses')
        .insert({
          id: tempChatId, // Use the same ID we used for storage paths
          user_id: user.id,
          file_name: file.name,
          language,
          whisper_mode: whisperMode,
          context: context || null,
          storage_path: null, // No ZIP file stored
          processing_status: 'parsed',
          html_content: htmlContent,
          message_count: parsed.stats.totalMessages,
          image_count: parsed.stats.imageCount,
          audio_count: parsed.stats.audioCount,
          document_count: parsed.stats.documentCount,
          messages_data: messagesWithStorageUrls as any,
          advanced_settings: advancedSettings as any,
          cost_tracking: {
            total: estimatedUploadCost,
            breakdown: {
              upload: estimatedUploadCost,
              chatQueries: 0,
              rerunCaptions: 0,
            },
            lastUpdated: new Date().toISOString(),
          },
        } as any)
        .select()
        .single();

      if (insertError || !chatRecord) {
        throw insertError;
      }

      toast({
        title: "ההעלאה הצליחה",
        description: "מעבד את הצ'אט שלך...",
      });

      // Store chat ID for viewer
      sessionStorage.setItem('chatId', chatRecord.id);
      
      navigate("/viewer");
      
    } catch (error) {
      console.error("Upload error:", error);
      toast({
        title: "ההעלאה נכשלה",
        description: error instanceof Error ? error.message : "שגיאה בעיבוד הקובץ",
        variant: "destructive",
      });
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-subtle flex items-center justify-center py-8 px-4">
      <div className="w-full max-w-3xl">
        {/* Header */}
        <div className="text-center mb-8">
          <img 
            src={logo} 
            alt="WhatAI Logo" 
            className="w-16 h-16 mx-auto mb-4"
          />
          <h1 className="text-4xl font-bold text-primary mb-2">
            WhatAI
          </h1>
          <p className="text-muted-foreground text-lg">
            מנתח צ'אטים של WhatsApp עם AI
          </p>
        </div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center mb-8">
          <div className="flex items-center gap-2">
            {[1, 2, 3].map((step) => (
              <div key={step} className="flex items-center">
                <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 transition-all ${
                  step === currentStep 
                    ? 'border-primary bg-primary text-primary-foreground' 
                    : step < currentStep
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-muted bg-muted text-muted-foreground'
                }`}>
                  {step < currentStep ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-semibold">{step}</span>
                  )}
                </div>
                {step < 3 && (
                  <div className={`w-12 h-0.5 mx-2 ${
                    step < currentStep ? 'bg-primary' : 'bg-muted'
                  }`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Wizard Card */}
        <div className="bg-card rounded-3xl p-8 shadow-elegant">
          {/* Step 1: File Selection */}
          {currentStep === 1 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">בחר קובץ לניתוח</h2>
                <p className="text-muted-foreground">
                  העלה ייצוא WhatsApp (ZIP בלבד)
                </p>
              </div>

              {/* Drop Zone */}
              <div
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                className={`relative border-2 border-dashed rounded-xl p-12 text-center transition-all cursor-pointer ${
                  isDragging
                    ? 'border-primary bg-primary/5'
                    : file
                    ? 'border-primary bg-primary/5'
                    : 'border-muted hover:border-primary hover:bg-primary/5'
                }`}
                onClick={() => document.getElementById('file-input')?.click()}
              >
                <input
                  id="file-input"
                  type="file"
                  accept=".zip"
                  onChange={handleFileChange}
                  className="hidden"
                />
                
                {!file ? (
                  <div className="flex flex-col items-center gap-4">
                    <div className="relative">
                      <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                        <UploadIcon className="w-10 h-10 text-primary" />
                      </div>
                      <div className="absolute -bottom-2 -right-2 w-10 h-10 rounded-full bg-primary flex items-center justify-center animate-bounce">
                        <ArrowDown className="w-5 h-5 text-primary-foreground" />
                      </div>
                    </div>
                    <div>
                      <p className="text-lg font-semibold mb-1">
                        גרור קובץ ZIP לכאן
                      </p>
                      <p className="text-sm text-muted-foreground">
                        או לחץ לבחירת קובץ
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-3">
                    <CheckCircle className="w-12 h-12 text-primary" />
                    <div>
                      <p className="font-semibold text-lg">{file.name}</p>
                      <p className="text-sm text-muted-foreground">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setFile(null);
                        setFileStats(null);
                      }}
                    >
                      החלף קובץ
                    </Button>
                  </div>
                )}
              </div>

              {analyzing && (
                <div className="text-center text-sm text-muted-foreground py-4 animate-pulse">
                  מנתח את תוכן הקובץ...
                </div>
              )}

              {/* How to Export Instructions */}
              <div className="mt-8 border-t pt-6">
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="export-guide">
                    <AccordionTrigger className="text-base font-semibold">
                      איך מייצאים צ'אט מ-WhatsApp?
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-6 pt-4">
                        {/* Android */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Smartphone className="w-5 h-5 text-primary" />
                            <h4 className="font-semibold">אנדרואיד (Android)</h4>
                          </div>
                          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground pr-4">
                            <li>פתח את הצ'אט ב-WhatsApp</li>
                            <li>לחץ על שלוש הנקודות (⋮) בפינה הימנית העליונה</li>
                            <li>בחר "עוד" (More)</li>
                            <li>בחר "ייצוא צ'אט" (Export chat)</li>
                            <li>בחר "כולל מדיה" (Include media) - חשוב!</li>
                            <li>שמור את קובץ ה-ZIP</li>
                          </ol>
                        </div>

                        {/* iPhone */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Smartphone className="w-5 h-5 text-primary" />
                            <h4 className="font-semibold">אייפון (iPhone)</h4>
                          </div>
                          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground pr-4">
                            <li>פתח את הצ'אט ב-WhatsApp</li>
                            <li>לחץ על שם איש הקשר או הקבוצה בראש המסך</li>
                            <li>גלול למטה ובחר "ייצוא צ'אט" (Export Chat)</li>
                            <li>בחר "צרף מדיה" (Attach Media) - חשוב!</li>
                            <li>שמור את קובץ ה-ZIP</li>
                          </ol>
                        </div>

                        {/* Web */}
                        <div className="space-y-2">
                          <div className="flex items-center gap-2">
                            <Globe className="w-5 h-5 text-primary" />
                            <h4 className="font-semibold">WhatsApp Web</h4>
                          </div>
                          <ol className="list-decimal list-inside space-y-1 text-sm text-muted-foreground pr-4">
                            <li>פתח את הצ'אט ב-WhatsApp Web</li>
                            <li>לחץ על שלוש הנקודות (⋮) בפינה הימנית העליונה</li>
                            <li>בחר "עוד" (More)</li>
                            <li>בחר "ייצוא צ'אט" (Export chat)</li>
                            <li>בחר "כולל מדיה" (With media) - חשוב!</li>
                            <li>שמור את קובץ ה-ZIP</li>
                          </ol>
                        </div>

                        <div className="bg-primary/10 rounded-lg p-4 mt-4">
                          <p className="text-sm font-medium text-primary">
                            💡 חשוב: ודא שבחרת "כולל מדיה" כדי שה-AI יוכל לנתח תמונות ואודיו
                          </p>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </div>
            </div>
          )}

          {/* Step 2: Language Selection */}
          {currentStep === 2 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">בחר שפה</h2>
                <p className="text-muted-foreground">
                  באיזו שפה תרצה לקבל את הסיכום והניתוח?
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="language" className="text-base font-semibold">
                  שפה
                </Label>
                <Select value={language} onValueChange={setLanguage}>
                  <SelectTrigger className="transition-smooth focus:shadow-soft h-12 text-base">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="he">עברית (Hebrew)</SelectItem>
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="ar">العربية (Arabic)</SelectItem>
                    <SelectItem value="es">Español (Spanish)</SelectItem>
                    <SelectItem value="fr">Français (French)</SelectItem>
                    <SelectItem value="de">Deutsch (German)</SelectItem>
                    <SelectItem value="zh">中文 (Chinese)</SelectItem>
                    <SelectItem value="ja">日本語 (Japanese)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}

          {/* Step 3: Context */}
          {currentStep === 3 && (
            <div className="space-y-6">
              <div>
                <h2 className="text-2xl font-bold mb-2">הוסף הקשר (אופציונלי)</h2>
                <p className="text-muted-foreground">
                  ספק כל מידע נוסף שיעזור ל-AI להבין טוב יותר את הצ'אט
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="context" className="text-base font-semibold">
                  הקשר
                </Label>
                <Textarea
                  id="context"
                  placeholder="לדוגמה: זהו צ'אט של קבוצת עבודה, צ'אט משפחתי, דיון על פרויקט מסוים..."
                  value={context}
                  onChange={(e) => setContext(e.target.value)}
                  className="resize-none transition-smooth focus:shadow-soft min-h-[150px] text-base"
                />
              </div>

              {file && fileStats && (
                <div className="p-4 bg-muted rounded-lg">
                  <p className="text-sm font-medium mb-2">סיכום:</p>
                  <div className="text-sm text-muted-foreground space-y-1">
                    <p>📁 קובץ: {file.name}</p>
                    <p>🌐 שפה: {language === 'he' ? 'עברית' : language === 'en' ? 'English' : language}</p>
                    {fileStats.imageCount > 0 && <p>🖼️ תמונות: {fileStats.imageCount}</p>}
                    {fileStats.audioCount > 0 && <p>🎵 אודיו: {fileStats.audioCount}</p>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex items-center justify-between mt-8 gap-4">
            <Button
              variant="outline"
              onClick={handlePrevStep}
              disabled={currentStep === 1 || uploading}
              className="w-full"
            >
              <ArrowRight className="w-4 h-4 ml-2" />
              הקודם
            </Button>

            {currentStep < 3 ? (
              <Button
                onClick={handleNextStep}
                disabled={!file || analyzing}
                className="w-full gradient-primary text-white"
              >
                הבא
                <ArrowLeft className="w-4 h-4 mr-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={uploading || analyzing || !fileStats}
                className="w-full gradient-primary text-white"
              >
                {uploading ? "מעבד..." : "נתח צ'אט"}
                <CheckCircle className="w-4 h-4 mr-2" />
              </Button>
            )}
          </div>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          המידע שלך מעובד בצורה מאובטחת. אנו משתמשים ב-AI כדי לחלץ תובנות מתמונות, אודיו ומסמכים.
        </p>
      </div>

      {/* Cost Confirmation Dialog */}
      <CostConfirmDialog
        open={showCostDialog}
        onOpenChange={setShowCostDialog}
        onConfirm={proceedWithUpload}
        cost={estimatedUploadCost}
        title="אישור העלאה"
        description="פעולה זו תעבד את הצ'אט שלך ב-WhatsApp, כולל ניתוח AI של תמונות וקבצי אודיו."
        currentBudget={currentBudget}
        breakdown={fileStats ? {
          imageCount: fileStats.imageCount,
          audioCount: fileStats.audioCount,
          videoCount: fileStats.videoCount,
          documentCount: fileStats.documentCount,
          contactCount: fileStats.contactCount,
          summaryIncluded: true,
        } : undefined}
      />
    </div>
  );
};

export default Upload;
